/**
 * Integrytas Group - CSM
 * §2 routed-write helpers — the sanctioned client surface for business mutations.
 *
 * Every business write must funnel through the Common Routine (§2). These thin
 * helpers wrap executeCommonRoutine() in the shapes call sites actually use, and
 * return a supabase-style `{ error }` envelope so existing
 * `const { error } = await …; if (error) throw error;` blocks convert with minimal
 * churn. The routine adds forensic stamping, optimistic concurrency, audit ledger
 * and PHE hooks over what a direct apiDb.from().insert/update/delete did.
 *
 * Single-row by primary key is the routine's contract. Multi-row-by-filter updates
 * (e.g. .eq().in().is()) have NO single-routine equivalent — resolve the matching
 * PKs first (a read via apiDb is fine) then call routedUpdate per PK. Do NOT reach
 * back to a direct write; the gate (scripts/check_write_path.mjs) will flag it.
 */

import type { AuthSession } from '../types/auth';
import { getForensicActorId } from '../types/auth';
import { executeCommonRoutine, type CommonRoutineOp, type OnMissingLookup } from './common_routine';

export interface RoutedError { message: string; code?: string; }
/** row_id is the new/affected row's PK. Typed number|null for the common bigint
 *  case; uuid-keyed modules (CMS, EDS) carry the uuid string at runtime — wrap in
 *  String(row_id) at those call sites. */
export interface RoutedResult { row_id: number | null; error: RoutedError | null; }

interface RoutedOpts {
  /** Auto-create missing lookup codes ('C') vs reject the row ('R', default). */
  on_missing_lookup?: OnMissingLookup;
  /** updated_at the client held at load time, for optimistic concurrency on CHANGE. */
  row_version?: string | null;
}

interface ActorCtx { actor_user_id: number | null; session_token: string | null; }

function actorCtx(session: AuthSession): ActorCtx {
  return {
    actor_user_id: getForensicActorId(session),
    session_token: session.session_token ?? null,
  };
}

async function run(
  ctx: ActorCtx,
  table: string,
  operation: CommonRoutineOp,
  row: Record<string, unknown>,
  keyColumns: string[] | undefined,
  opts: RoutedOpts,
): Promise<RoutedResult> {
  const res = await executeCommonRoutine({
    table_name: table,
    operation,
    row,
    key_columns: keyColumns,
    on_missing_lookup: opts.on_missing_lookup ?? 'R',
    row_version: opts.row_version ?? null,
    ...ctx,
  });
  if (res.result_code === 'OK') return { row_id: res.row_id, error: null };
  // VERSION_CONFLICT / PENDING_APPROVAL / REJECTED — surface a single error line (§17.B7).
  const first = res.errors[0];
  const message =
    res.result_code === 'VERSION_CONFLICT'
      ? 'This record changed since you opened it. Reload and retry.'
      : first?.message ?? `Write rejected (${res.result_code}).`;
  return { row_id: res.row_id, error: { message, code: first?.code } };
}

/** ADD a single row. Returns the new row_id. */
export function routedInsert(
  session: AuthSession, table: string, row: Record<string, unknown>, opts: RoutedOpts = {},
): Promise<RoutedResult> {
  return run(actorCtx(session), table, 'ADD', row, undefined, opts);
}

/** CHANGE a single row addressed by its primary key. */
export function routedUpdate(
  session: AuthSession, table: string, pkColumn: string, pkValue: unknown,
  patch: Record<string, unknown>, opts: RoutedOpts = {},
): Promise<RoutedResult> {
  return run(actorCtx(session), table, 'CHANGE', { ...patch, [pkColumn]: pkValue }, [pkColumn], opts);
}

/** DELETE a single row addressed by its primary key. */
export function routedDelete(
  session: AuthSession, table: string, pkColumn: string, pkValue: unknown, opts: RoutedOpts = {},
): Promise<RoutedResult> {
  return run(actorCtx(session), table, 'DELETE', { [pkColumn]: pkValue }, [pkColumn], opts);
}

/** CHANGE a single row addressed by a COMPOSITE key (e.g. a `_tl` row keyed by
 *  page_id + locale_code). `row` must carry every key column plus the patch. */
export function routedUpdateBy(
  session: AuthSession, table: string, keyColumns: string[], row: Record<string, unknown>, opts: RoutedOpts = {},
): Promise<RoutedResult> {
  return run(actorCtx(session), table, 'CHANGE', row, keyColumns, opts);
}

/** ADD many rows sequentially; stops and returns the first error. */
export async function routedInsertMany(
  session: AuthSession, table: string, rows: Record<string, unknown>[], opts: RoutedOpts = {},
): Promise<RoutedResult> {
  let last: RoutedResult = { row_id: null, error: null };
  for (const row of rows) {
    last = await routedInsert(session, table, row, opts);
    if (last.error) return last;
  }
  return last;
}

// ---------------------------------------------------------------------------
// Actor-based variants — for service/non-UI contexts that hold only a numeric
// actor id (no AuthSession). The routine stamps modified_by from actor_user_id;
// session_token is null (acceptable — forensic actor still recorded).
// ---------------------------------------------------------------------------

// actorId may be null for system/unattended contexts — the routine records a null
// modified_by (no authenticated actor), same as a direct write with modified_by:null.

/** ADD a single row as a known actor id (or null for system context). */
export function routedInsertAs(
  actorId: number | null, table: string, row: Record<string, unknown>, opts: RoutedOpts = {},
): Promise<RoutedResult> {
  return run({ actor_user_id: actorId, session_token: null }, table, 'ADD', row, undefined, opts);
}

/** CHANGE a single row (by PK) as a known actor id (or null). */
export function routedUpdateAs(
  actorId: number | null, table: string, pkColumn: string, pkValue: unknown,
  patch: Record<string, unknown>, opts: RoutedOpts = {},
): Promise<RoutedResult> {
  return run({ actor_user_id: actorId, session_token: null }, table, 'CHANGE',
    { ...patch, [pkColumn]: pkValue }, [pkColumn], opts);
}

/** DELETE a single row (by PK) as a known actor id (or null). */
export function routedDeleteAs(
  actorId: number | null, table: string, pkColumn: string, pkValue: unknown, opts: RoutedOpts = {},
): Promise<RoutedResult> {
  return run({ actor_user_id: actorId, session_token: null }, table, 'DELETE',
    { [pkColumn]: pkValue }, [pkColumn], opts);
}
